import { InventoryItem } from "@/types/inventory";
import { formatDate } from "@/utils/helpers";

interface Props {
    item: InventoryItem;
}

const InventoryDetails: React.FC<Props> = ({ item }) => {
    const { inventory } = item;

    return (
        <div className="w-full max-w-6xl mx-auto my-12 flex flex-col md:flex-row gap-8 items-stretch">
            <div className="flex-1 min-w-0 bg-white rounded-lg border border-gray-200 p-8 flex flex-col items-start shadow-sm">
                <div className="flex items-center gap-6 w-full mb-6">
                    {inventory.thumbnail && (
                        <img
                            src={inventory.thumbnail}
                            alt={inventory.title}
                            className="w-24 h-24 rounded-lg object-cover border border-gray-200"
                        />
                    )}
                    <div>
                        <h2 className="font-bold text-2xl mb-1 text-gray-800">
                            {inventory.title}
                        </h2>
                        <div className="text-gray-500 text-sm">
                            SKU: <span className="text-gray-700">{inventory.sku}</span>
                        </div>
                    </div>
                </div>
                <div className="text-gray-600 text-base mb-2">
                    {inventory.description || (
                        <span className="text-gray-400 italic">No description</span>
                    )}
                </div>
                <div className="flex flex-wrap gap-8 mb-6 text-sm text-gray-700">
                    <div>
                        <span className="font-medium">Weight:</span> {inventory.weight}g
                    </div>
                    {inventory.length && (
                        <div>
                            <span className="font-medium">Length:</span> {inventory.length}cm
                        </div>
                    )}
                    {inventory.width && (
                        <div>
                            <span className="font-medium">Width:</span> {inventory.width}cm
                        </div>
                    )}
                    {inventory.height && (
                        <div>
                            <span className="font-medium">Height:</span> {inventory.height}cm
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4 w-full text-base text-gray-700 mb-6">
                    <div className="font-medium">Available:</div>
                    <div className="text-right">
                        {inventory.location_levels.reduce(
                            (sum, lvl) => sum + (lvl.available_quantity || 0),
                            0
                        )}
                    </div>
                    <div className="font-medium">Shipping:</div>
                    <div className="text-right">
                        {inventory.requires_shipping ? "Yes" : "No"}
                    </div>
                    <div className="font-medium">Origin:</div>
                    <div className="text-right">
                        {inventory.origin_country || (
                            <span className="text-gray-400">N/A</span>
                        )}
                    </div>
                    <div className="font-medium">HS Code:</div>
                    <div className="text-right">
                        {inventory.hs_code || <span className="text-gray-400">N/A</span>}
                    </div>
                    <div className="font-medium">MID Code:</div>
                    <div className="text-right">
                        {inventory.mid_code || <span className="text-gray-400">N/A</span>}
                    </div>
                    <div className="font-medium">Material:</div>
                    <div className="text-right">
                        {inventory.material || <span className="text-gray-400">N/A</span>}
                    </div>
                </div>
                <div className="w-full mb-8">
                    <div className="text-base text-gray-600 mb-2 font-medium">
                        Stock Locations
                    </div>
                    {inventory.location_levels.length === 0 ? (
                        <span className="text-gray-400 text-sm">No locations</span>
                    ) : (
                        <div className="space-y-3">
                            {inventory.location_levels.map((lvl) => (
                                <div
                                    key={lvl.id}
                                    className="text-sm flex flex-row gap-8 items-center bg-gray-50 rounded border border-gray-100 p-3"
                                >
                                    <span className="font-semibold">
                                        {lvl.stock_locations[0]?.name || "Unknown"}
                                    </span>
                                    <span>
                                        Available:{" "}
                                        <span className="font-medium">
                                            {lvl.available_quantity}
                                        </span>
                                    </span>
                                    <span>
                                        Stocked:{" "}
                                        <span className="font-medium">{lvl.stocked_quantity}</span>
                                    </span>
                                    <span>
                                        Reserved:{" "}
                                        <span className="font-medium">{lvl.reserved_quantity}</span>
                                    </span>
                                    <span>
                                        Incoming:{" "}
                                        <span className="font-medium">{lvl.incoming_quantity}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="text-gray-400 text-xs text-left">
                    <div>
                        Created:{" "}
                        <span className="font-medium">
                            {formatDate(inventory.created_at)}
                        </span>
                    </div>
                    <div>
                        Updated:{" "}
                        <span className="font-medium">
                            {formatDate(inventory.updated_at)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex-1 min-w-0 bg-white rounded-lg border border-gray-200 p-8 flex flex-col items-start shadow-sm">
                <div className="text-center w-full">
                    <h2 className="mb-4 font-bold text-2xl text-gray-800">
                        Inventory history
                    </h2>
                    <div className="text-gray-400 text-lg">Coming soon...</div>
                </div>
            </div>
        </div>
    );
};

export default InventoryDetails;
